import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { DropdownList } from 'react-widgets';
import { DateInput } from '../../../components/form-group';
import { combineDateAndTime } from '../../../utils/CombineDateAndTime';
import {
  createStartOfDayWithTimeZone,
  DateFormats,
  getDayDifference,
  getMinuteDifference,
  timeBuilder,
} from '../../utils';

interface ICustomTimeInputProps {
  defaultValue?: ITimePeriodData;
  onChange?: (value: ITimePeriodData) => void;
  timeGap?: number;
  gap?: number;
}

export const CustomTimePeriod: React.FunctionComponent<ICustomTimeInputProps> = (
  props
) => {
  const [endData, setEndData] = useState<{ label: string; value: string }[]>(
    []
  );
  const [startData, setStartData] = useState<
    { label: string; value: string }[]
  >([]);
  const startOfToday = new Date();
  const [startDate, setStartDate] = useState<Date>(
    props.defaultValue?.startDate ?? startOfToday
  );
  const [startTime, setStartTime] = useState(
    props.defaultValue?.startTime ?? ''
  );
  const [endTime, setEndTime] = useState(props.defaultValue?.endTime ?? '');
  const [endDate, setEndDate] = useState<Date>(
    props.defaultValue?.endDate ?? props.defaultValue?.startDate ?? startOfToday
  );
  React.useEffect(() => {
    if (startData.length === 0) {
      setStartData(timeBuilder(undefined, props.gap));
      setEndDropdownData();
    }
    setRendered(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    setEndDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime]);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (rendered) {
      handleOnChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime]);

  const handleOnChange = () => {
    const value: ITimePeriodData = {
      startDate: createStartOfDayWithTimeZone(startDate),
      startTime: startTime ? startTime : undefined,
      endDate: createStartOfDayWithTimeZone(endDate),
      endTime: endTime ? endTime : undefined,
    };
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const setEndDropdownData = () => {
    if (startDate != null && endDate != null && startTime !== '') {
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf('day').toDate(),
        startTime
      );
      if (moment(startDate).isSame(moment(endDate), 'day')) {
        setEndData(timeBuilder(startDateTime, props.gap));
      } else {
        setEndData(timeBuilder(undefined, props.gap));
      }
    }
  };

  React.useEffect(() => {
    if (endDate != null && endTime !== '' && endData.length > 0) {
      const isValidObject = endData.find((p) => p.value === endTime) != null;
      if (!isValidObject) {
        setEndTime(endData[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endData]);

  const handleStartDateChange = (date: Date) => {
    date = combineDateAndTime(date, startTime);
    const dayGap = getDayDifference(startDate, endDate);
    setStartDate(date);
    setEndDate(moment(date).add(dayGap, 'days').toDate());
  };

  const handleStartTimeChange = (time: string) => {
    if (
      endTime !== '' &&
      endDate !== undefined &&
      startTime !== '' &&
      startDate !== undefined
    ) {
      const endDateTime = combineDateAndTime(
        moment(endDate).startOf('day').toDate(),
        endTime
      );
      const startDateTime = combineDateAndTime(
        moment(startDate).startOf('day').toDate(),
        startTime
      );
      const minuteGap = getMinuteDifference(startDateTime, endDateTime);

      const newStartDateTime = combineDateAndTime(startDate, time);
      const newEndDateTime = moment(newStartDateTime).add(minuteGap, 'minutes');
      setEndTime(newEndDateTime.format(DateFormats.TimeOnlyFormat));
      const newEndDate = moment(
        moment(newEndDateTime).utc(true).format(DateFormats.NormalDateFormat)
      )
        .utc(true)
        .toDate();
      setEndDate(newEndDate);
    }
    setStartTime(time);
  };

  return (
    <div className="custom-time-input">
      <div className="row gutter-8">
        <div className="col-lg-3 col-12 mb-lg-0 mb-3 time-period">
          <DateInput
            default={startDate}
            placeholder={'dd/mm/yyyy'}
            //   minDate={watch('startDate')}
            onChange={(date) => {
              handleStartDateChange(date);
            }}
          />
        </div>
        <div className="col mb-lg-0 mb-3 time-period-start">
          <div className="position-relative time-period">
            <DropdownList
              placeholder={'HH:mm'}
              containerClassName="time-period-container"
              textField="label"
              valueField="value"
              //  placeholder={props.placeHolder}
              value={startTime}
              onChange={(val) => {
                handleStartTimeChange(val.value);
              }}
              data={startData}
            />
            {startTime !== '' && (
              <i
                onClick={() => {
                  setStartTime('');
                }}
                className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
              ></i>
            )}
          </div>
        </div>
        <div className="px-0 align-self-center font-xxl pb-2 mb-lg-0 mb-3 px-1">
          -
        </div>
        <div className="col mb-sm-0 mb-3">
          <div className="position-relative time-period">
            <DropdownList
              placeholder={'HH:mm'}
              containerClassName="time-period-container"
              textField="label"
              valueField="value"
              //  placeholder={props.placeHolder}
              value={endTime}
              onChange={(val) => {
                setEndTime(val.value);
              }}
              data={endData}
            />
            {endTime !== '' && (
              <i
                onClick={() => {
                  setEndTime('');
                }}
                className="icon-close position-absolute bg-white align-y-center font-xxxs text-center p-2 font-weight-bold cursor-pointer time-close"
              ></i>
            )}
          </div>
        </div>
        <div className="col-lg-3 col-12 time-period">
          <DateInput
            default={endDate}
            placeholder={'dd/mm/yyyy'}
            minDate={startDate}
            onChange={(date) => {
              setEndDate(date);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const isTimePeriodValid = (period: ITimePeriodData) => {
  return (
    period?.startDate && period?.endDate && period?.startTime && period.endTime
  );
};

export interface ITimePeriodData {
  startDate?: Date;
  startTime?: string;
  endDate?: Date;
  endTime?: string;
}
